import { useEffect, useState, type JSX } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../../lib/cn.js';
import { resolveAssetUrl } from './image-source.js';

export interface FigureProps {
  src: string;
  alt: string;
  caption?: string;
  /** Checkerboard backing so transparent (cut-out) PNGs read on the dark canvas. */
  transparent?: boolean;
  /** Click-to-zoom into a full-screen lightbox. Default true. */
  zoom?: boolean;
  /** Eager-load (use for an above-the-fold hero). Default false = lazy. */
  priority?: boolean;
  className?: string;
}

export function Figure({
  src,
  alt,
  caption,
  transparent = false,
  zoom = true,
  priority = false,
  className,
}: FigureProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const url = resolveAssetUrl(src);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const imgClass = cn(
    'block w-full rounded-lg border border-white/[0.06]',
    transparent && 'checkerboard',
  );

  return (
    <figure className={cn('my-5', className)}>
      {zoom ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Zoom image: ${alt}`}
          className="block w-full cursor-zoom-in rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          <img
            src={url}
            alt={alt}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            className={imgClass}
          />
        </button>
      ) : (
        <img
          src={url}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          className={imgClass}
        />
      )}

      {caption ? (
        <figcaption className="mt-2.5 text-center text-[13px] leading-[1.5] text-muted">
          {caption}
        </figcaption>
      ) : null}

      {zoom && open ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={alt}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-50 flex cursor-zoom-out items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
        >
          <img
            src={url}
            alt={alt}
            className={cn(
              'max-h-[90vh] w-auto max-w-[94vw] rounded-lg',
              transparent && 'checkerboard',
            )}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            aria-label="Close image"
            className="absolute right-4 top-4 rounded-md p-2 text-white/80 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </figure>
  );
}
